import { TIoTDevice, TIoTProvider, TIoTWebhook } from '@treedis/tour-loader';
import { useCallback, useEffect, useRef, useState } from 'react';
import { io } from 'socket.io-client';
import { VSTourAsset } from '@/_models/virtualStaging/VSObject';
import PublicAPI from '../../api/TreedisPublicDAO';
import { getDeviceMediaImage, iotToast } from './config';

type Props = {
  showcase: any;
  tourId: number;
  iot: {
    sceneAssets: VSTourAsset[];
    lockDeviceId?: number;
    moveToDevice?: TIoTDevice;
  };
  updateIotState: (state: any) => void;
  setState: (state: any) => void;
};

export default function IotLogic({ showcase: showcaseProp, iot, tourId, updateIotState, setState }: Props) {
  const [devices, setDevices] = useState<TIoTDevice[]>();
  const [providers, setProviders] = useState<TIoTProvider[]>();
  const { scene, showcase } = showcaseProp;
  const { sceneAssets, lockDeviceId, moveToDevice } = iot;

  useEffect(() => {
    (async () => {
      const devicesRes = await PublicAPI.getIoTDevices({
        query: {
          tourId,
        },
      });
      setDevices(devicesRes?.data);
      updateIotState({ devices: devicesRes?.data });

      const providersRes = await PublicAPI.getIoTProviders({
        query: {
          tourId,
        },
      });
      setProviders(providersRes?.data);
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const iotRenderedAssets = useRef<VSTourAsset[]>([]);

  const replaceImageSceneNode = useCallback(
    async (asset: VSTourAsset) => {
      // If we get newAsset, it means we get a new scene vsImage to replace
      scene?.sceneImage?.remove(asset);
      await scene?.sceneImage?.create(asset);
    },
    [scene?.sceneImage],
  );

  const updateStateDevices = useCallback(
    (deviceId: number, key: string, value: boolean) => {
      setDevices((prev) => {
        const devicesClone = [...(prev || [])];
        const idx = devicesClone.findIndex((d) => d.id === deviceId || d.providerDeviceId === deviceId);
        if (idx !== -1) {
          devicesClone[idx][key] = value;
        }
        updateIotState({ devices: devicesClone }); // BaseTour iot devices .. needed for iotMenu
        return devicesClone;
      });
    },
    [updateIotState],
  );

  const handleLockEvent = useCallback(
    (data: TIoTWebhook, locked: boolean) => {
      iotToast(`${data.doorName} door was ${data.doorAction}ed`, locked ? 'locked' : 'unlocked');
      updateStateDevices(data.doorId, 'unlocked', !locked);
    },
    [updateStateDevices],
  );

  const handleLockdownEvent = useCallback(
    (data: TIoTWebhook, locked: boolean) => {
      iotToast(`Lockdown was ${locked ? 'activated' : 'cancelled'} for ${data.doorName} door`, locked ? 'lockdown' : 'locked');
      updateStateDevices(data.doorId, 'lockedDown', locked);
    },
    [updateStateDevices],
  );

  const handleMoveToLock = useCallback(
    async (d: TIoTDevice) => {
      updateIotState({
        moveToDevice: null,
      });

      if (!d.toursVsImageId) return;
      const vsObject = sceneAssets.find((o) => o.id === d.toursVsImageId);

      if (!vsObject) return;
      showcase?.Sweep?.moveTo(vsObject.sweepUuid, {
        rotation: {
          x: vsObject.srX,
          y: vsObject.srY,
          z: vsObject.srZ,
        },
        transition: showcase?.Sweep?.Transition.FLY,
      });
    },
    [sceneAssets, updateIotState, showcase?.Sweep],
  );

  const handleUnlockAgree = useCallback(
    async (d: TIoTDevice, code?: string) => {
      const promise = !d.unlocked ? PublicAPI.unlockIoTDevice : undefined;

      const res = await promise({
        data: {
          tourId,
          deviceId: d.id,
          ...(code && { passCode: Number(code) }),
        },
      });

      setState({ confirmDialog: { open: false } });
      if (res?.message !== 'Success.') {
        // Not ideal to check for message, but the status code is too generic
        if (res?.message === 'Wrong Pass Code') {
          iotToast('Wrong code!', undefined, 'error');
        } else {
          iotToast(res?.message, undefined, 'error');
        }
        return;
      }

      updateStateDevices(d.id, 'unlocked', true);
    },
    [setState, tourId, updateStateDevices],
  );

  const handleUnlock = useCallback(
    async (d: TIoTDevice) => {
      updateIotState({
        lockDeviceId: null,
      });

      if (d.lockedDown) {
        iotToast(`${d.name} door is on lockdown`, 'lockdown');
        return;
      }

      if (d.unlocked) {
        iotToast(`${d.name} door will automatically lock soon`, undefined, 'info');
        return;
      }

      const hasCode = providers?.some((p) => p.hasPassCode);
      const title = 'Confirm action';
      const desc = `Are you sure you want to <br /> ${!d.unlocked ? 'unlock' : 'lock'} ${d.name} door?`;

      setState({
        confirmDialog: {
          open: true,
          title,
          desc,
          showInput: hasCode,
          onAgree: async (code?: string) => handleUnlockAgree(d, code),
          onCancel: () => {
            setState({ confirmDialog: { open: false } });
          },
        },
      });
    },
    [handleUnlockAgree, providers, setState, updateIotState],
  );

  const connectIotSocket = useCallback(() => {
    const iotSocket = io(process.env.REACT_APP_DOMAIN, {
      transports: ['websocket'],
      autoConnect: true,
    });

    iotSocket.on('lock-device', async (data) => {
      if (data.tourIds?.includes(tourId)) {
        handleLockEvent(data, true);
      }
    });

    iotSocket.on('unlock-device', async (data) => {
      if (data.tourIds?.includes(tourId)) {
        handleLockEvent(data, false);
      }
    });

    iotSocket.on('lockdown-device', async (data) => {
      if (data.tourIds?.includes(tourId)) {
        handleLockdownEvent(data, true);
      }
    });

    iotSocket.on('unlockdown-device', async (data) => {
      if (data.tourIds?.includes(tourId)) {
        handleLockdownEvent(data, false);
      }
    });
  }, [handleLockEvent, handleLockdownEvent, tourId]);

  useEffect(() => {
    if (lockDeviceId) {
      const lockDevice = devices?.find((d) => d.id === lockDeviceId || d.toursVsImageId === lockDeviceId);
      if (lockDevice) handleUnlock(lockDevice);
    }
  }, [lockDeviceId, handleUnlock, devices]);

  useEffect(() => {
    if (moveToDevice) {
      handleMoveToLock(moveToDevice);
    }
  }, [moveToDevice, handleMoveToLock]);

  useEffect(() => {
    connectIotSocket();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Updating scene IoT images/tags
  useEffect(() => {
    if (!scene) return;

    devices?.forEach((d) => {
      if (!d.toursVsImageId) return;

      const asset = sceneAssets?.find((a) => a.id === d.toursVsImageId);
      if (!asset) return;

      const mediaImage = getDeviceMediaImage(d);

      // If asset is already rendered, we need to check if we need to replace it with a new one
      const renderedAsset = iotRenderedAssets.current?.find((a) => a.id === asset.id);
      if (mediaImage?.title !== renderedAsset?.details?.title) {
        // @ts-ignore
        const newAsset: VSTourAsset = { ...asset, details: mediaImage };
        replaceImageSceneNode(newAsset);

        const index = iotRenderedAssets.current.findIndex((a) => a.id === asset.id);
        if (index !== -1) {
          iotRenderedAssets.current[index] = newAsset as VSTourAsset;
        } else {
          iotRenderedAssets.current.push(newAsset);
        }
      }
    });
  }, [devices, scene, sceneAssets, replaceImageSceneNode]);

  return <></>;
}
